import React, { useEffect, useMemo, useState } from 'react';
import ProjectListTable from './UsersListTable';
import ProjectListSearch from './UsersListSearch';
import ProjectTeamFilter from './UsersTeamFilter';
import ProjectAccessFilter from './UsersAccessFilter';
import ProjectTablePagination from './UsersTablePagination';
import { CircularProgress } from '@mui/material';
import styles from './Users.module.css';
import { Outlet, useLocation } from 'react-router-dom';
import SearchBar from '../../../Components/SearchBar/SearchBar';
import { useDispatch, useSelector } from '../../../redux/store';
import { fetchAdminEmployeeList } from '../../../redux/slices/adminUsers';

function Users() {
  const dispatch = useDispatch();
  const adminUsers = useSelector((s) => s.adminUsers);
  const allRows = Array.isArray(adminUsers.rows) ? adminUsers.rows : [];
  const totalRows = adminUsers.totalRows || 0;
  const loading = adminUsers.loading;

  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [accessFilter, setAccessFilter] = useState('1');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const [paginationLoading, setPaginationLoading] = useState(false);

  useEffect(() => {
    setPaginationLoading(true);
    const promise = dispatch(fetchAdminEmployeeList({
      page: currentPage,
      rowsPerPage,
      searchTerm,
      teamFilter,
      sortColumn,
      sortDirection,
      accessFilter,
    }));

    return () => {
      promise?.abort?.();
    };
  }, [dispatch, searchTerm, teamFilter, sortColumn, sortDirection, currentPage, rowsPerPage, accessFilter]);

  useEffect(() => {
    if (!loading) setPaginationLoading(false);
  }, [loading]);
  
  const handleSearch = (event) => {
    setInputValue(event.target.value);
  };

  const handleSearchSubmit = () => {
    setSearchTerm(inputValue);
    setCurrentPage(1);
  };

  const handleTeamFilter = (team) => {
    setTeamFilter(team.toLowerCase());
    setCurrentPage(1);
  };

  const handleAccessFilter = (access) => {
    setAccessFilter(access);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  useEffect(() => {
    let filteredRows = allRows.filter((row) =>
      Object.values(row).some((value) =>
        value ? value.toString().toLowerCase().includes(searchTerm.toLowerCase()) : false
      )
    ).filter((row) => 
      teamFilter === '' || (row.team && row.team.toLowerCase().includes(teamFilter.toLowerCase()))
    );

    let sortedRows = [...filteredRows].sort((a, b) => {
      if (sortDirection === 'asc') {
        return a[sortColumn] > b[sortColumn] ? 1 : -1;
      } else {
        return a[sortColumn] < b[sortColumn] ? 1 : -1;
      }
    });

    setRows(sortedRows);
  }, [allRows, sortColumn, sortDirection, searchTerm, teamFilter]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setPaginationLoading(true);
      setCurrentPage(currentPage - 1);
    }
  };
  
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setPaginationLoading(true);
      setCurrentPage(currentPage + 1);
    }
  };
  
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };
  
  
  const totalPages = totalRows > 0 ? Math.ceil(totalRows / rowsPerPage) : 0;
  const currentPageValue = totalRows > 0 ? currentPage : 0;

  // const data2 = [{client: "McAdam", comment: "Partner", fee: 'CURRENT', feeburn: '30 Years, 10 Months', id: 2, invoice: '12 Days', jobno: 27, nextdeadline: "Team Fields", projectaddress: "Suzy", promoted: "", runner: "James Nevin", status: "12", team: "Alpha 2"}]
  // const data = [{Ref: 27, FirstName: "Suzy", LastName: 'McAdam', Level: 12, JobTitle: 'Partner', LineManager: 'James Nevin', Team: "Team Fields", Age: '30 Years, 10 Months', RemainingHoliday: "12 Days", Status: "CURRENT"}]
  
  const location = useLocation();

  return (
    <div>            
      {location.pathname === "/Admin/Users" && (
        <div className={styles.projectContent}>
          <div className={styles.Projecttitle}>Users</div>
          <div className={styles.ProjectTableFilters}>
            <div className={styles.ProjectTeamFilter}>
              <label className={styles.FilterLabel} htmlFor='teamFilter' style={{ fontSize: '12px' }}>Team:</label>
              <ProjectTeamFilter id='teamFilter' onSelect={handleTeamFilter} disabled={loading} />
            </div>
            <div className={styles.ProjectStatusFilter}>
              <label className={styles.FilterLabel} htmlFor='accessFilter' style={{ fontSize: '12px' }}>Access:</label>
              <ProjectAccessFilter id='accessFilter' onSelect={handleAccessFilter} disabled={loading} value={accessFilter} />
            </div>
            <div className={styles.ProjectListSearch}>
              <SearchBar onChange={handleSearch} onKeyDown={handleKeyDown} disabled={loading} onClick={handleSearchSubmit} />
              {/* <button onClick={handleSearchSubmit} disabled={loading} className={loading ? 'disabled' : ''}>Search</button> */}
            </div>
          </div>

          {loading ? (
            <>

              <div className={styles.ProjectListBox}>
                <ProjectListTable
                  rows={[]} // Send empty rows while loading
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  loading={loading} // Send loading status to ProjectListTable
                />
              </div>

              <div className={styles.ProjectPagination}>
                <ProjectTablePagination
                  currentPage={currentPageValue}
                  totalPages={totalPages}
                  onPrevPage={paginationLoading ? null : handlePrevPage} // disable if loading
                  onNextPage={paginationLoading ? null : handleNextPage} // disable if loading
                  rowsShown={allRows.length}
                  totalRows={totalRows}
                  rowsPerPage={rowsPerPage}
                />
              </div>
            </>
          ) : (
            <>
              <div className={styles.ProjectListBox}>
                <ProjectListTable
                  rows={rows}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  loading={loading} // Send loading status to ProjectListTable
                />
              </div>

              {rows.length === 0 && <div className={styles.NoProjects}>THE PERSON YOU HAVE SEARCHED CANNOT BE FOUND</div>}
              
              <div className={styles.ProjectPagination}>
                <ProjectTablePagination
                  currentPage={currentPageValue}
                  totalPages={totalPages}
                  onPrevPage={paginationLoading ? null : handlePrevPage} // disable if loading
                  onNextPage={paginationLoading ? null : handleNextPage} // disable if loading
                  rowsShown={rows.length}
                  totalRows={totalRows}
                  rowsPerPage={rowsPerPage}
                />
              </div>
            </>
          )}
        </div>
      )}
      <Outlet />
    </div>
  );
}

export default Users;